import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "../App";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { ArrowLeft, ClipboardSignature } from "lucide-react";
import { handoverApi, MANAGE_ROLES, naturalSort } from "./handover/handoverApi";
import { DevelopersPanel } from "./handover/DevelopersPanel";
import { DwellingClientsList } from "./handover/DwellingClientsList";
import { DeveloperDialog } from "./handover/DeveloperDialog";
import { DwellingClientDialog } from "./handover/DwellingClientDialog";
import { BulkAssignBar } from "./handover/BulkAssignBar";
import { WalkthroughHistoryDialog } from "./handover/WalkthroughHistoryDialog";
import { ShareHandoverLinkDialog } from "./handover/ShareHandoverLinkDialog";

export default function ClientHandover() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(localStorage.getItem("handover_site") || "");
  const [developers, setDevelopers] = useState([]);
  const [dwellings, setDwellings] = useState([]);
  const [selected, setSelected] = useState([]);
  const [devDialog, setDevDialog] = useState({ open: false, developer: null });
  const [clientDwelling, setClientDwelling] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [historyDwelling, setHistoryDwelling] = useState(null);
  const [shareDwelling, setShareDwelling] = useState(null);

  useEffect(() => {
    if (!canManage) return;
    fetch(`${API}/sites`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((s) => {
        setSites(s);
        if (!s.some((x) => x.id === siteId)) setSiteId(s[0]?.id || "");
      });
  }, [canManage]);

  const load = useCallback(async () => {
    if (!siteId || !canManage) return;
    try {
      const [devs, rows] = await Promise.all([handoverApi(`/sites/${siteId}/developers`), handoverApi(`/sites/${siteId}/clients`)]);
      setDevelopers(devs);
      setDwellings([...rows].sort(naturalSort));
    } catch (e) {
      toast.error(e.message);
    }
  }, [siteId, canManage]);

  useEffect(() => { localStorage.setItem("handover_site", siteId); setSelected([]); load(); }, [siteId, load]);

  const deleteDeveloper = async (dev) => {
    if (!window.confirm(`Delete developer ${dev.company_name}?`)) return;
    try {
      await handoverApi(`/developers/${dev.id}`, { method: "DELETE" });
      toast.success("Developer deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!canManage) {
    return (
      <div className="p-8 space-y-4" data-testid="handover-no-access">
        <p className="text-slate-600">Client Handover is available to site managers, owners and admins.</p>
        <Button variant="outline" onClick={() => navigate("/defects")} data-testid="handover-no-access-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Back to Defects
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="client-handover-page">
      <div>
        <Button variant="ghost" onClick={() => navigate("/defects")} className="mb-2 -ml-2 text-slate-600" data-testid="handover-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" />Defects
        </Button>
        <h1 className="heading-primary text-3xl sm:text-4xl mb-1 flex items-center gap-3">
          <ClipboardSignature className="w-8 h-8 text-amber-600" />Client Walkthroughs
        </h1>
        <p className="text-slate-600">Client Handover — set who the client is for each dwelling before starting a walkthrough.</p>
      </div>

      <Select value={siteId} onValueChange={setSiteId}>
        <SelectTrigger className="sm:w-80 bg-white" data-testid="handover-site-select"><SelectValue placeholder="Choose a site" /></SelectTrigger>
        <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>

      {siteId && (
        <>
          <DevelopersPanel developers={developers} onAdd={() => setDevDialog({ open: true, developer: null })}
            onEdit={(d) => setDevDialog({ open: true, developer: d })} onDelete={deleteDeveloper} />
          <DwellingClientsList
            dwellings={dwellings} selected={selected}
            onSelect={(id, v) => setSelected((s) => (v ? [...s, id] : s.filter((x) => x !== id)))}
            onSelectAll={(v) => setSelected(v ? dwellings.map((d) => d.id) : [])}
            onSetClient={setClientDwelling}
            onHistory={setHistoryDwelling}
            onShare={setShareDwelling}
            bulkBar={<BulkAssignBar siteId={siteId} selected={selected} dwellings={dwellings} developers={developers}
              onDone={() => { setSelected([]); load(); }} />}
          />
        </>
      )}

      <DeveloperDialog open={devDialog.open} developer={devDialog.developer} siteId={siteId}
        onOpenChange={(open) => setDevDialog((s) => ({ ...s, open }))}
        onSaved={async () => {
          await load();
          if (returnTo) { setClientDwelling(returnTo); setReturnTo(null); }
        }} />
      <DwellingClientDialog open={!!clientDwelling} dwelling={clientDwelling} developers={developers}
        onOpenChange={(open) => !open && setClientDwelling(null)} onSaved={load}
        onAddDeveloper={() => { setReturnTo(clientDwelling); setClientDwelling(null); setDevDialog({ open: true, developer: null }); }} />
      <WalkthroughHistoryDialog dwelling={historyDwelling} onOpenChange={(o) => !o && setHistoryDwelling(null)} />
      <ShareHandoverLinkDialog dwelling={shareDwelling} onOpenChange={(o) => !o && setShareDwelling(null)} />
    </div>
  );
}
